import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import { useState } from 'react';
import { RECORD_STATUSES } from '@app/shared';
import type { TopupDto } from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { StatusChip } from '../../components/StatusChip';
import { formatCents } from '../../lib/format';
import { useListState } from '../../lib/useListState';
import { TopupFormDialog } from './TopupFormDialog';
import { topupsExportUrl, useTopups } from './api';
import { useCarriers } from './lookups';
import { STATE_OPTIONS, carrierOptions } from './options';

/** States shown in the "State" column before the rest moves into a tooltip. */
const STATES_SHOWN = 6;

function StateList({ states }: { states: string[] }) {
  const all = states.join(', ');
  if (states.length <= STATES_SHOWN) return <>{all}</>;
  return (
    <Tooltip title={all}>
      <span>
        {states.slice(0, STATES_SHOWN).join(', ')} +{states.length - STATES_SHOWN} more
      </span>
    </Tooltip>
  );
}

/** "Topup" tab of Plan Config. */
export function TopupsTab() {
  const list = useListState({ name: '', state: '', status: '', carrierId: '' }, 10, 'name');
  const topups = useTopups(list.params);
  const carriers = useCarriers();
  const [form, setForm] = useState<{ topup?: TopupDto } | null>(null);

  const columns: Column<TopupDto>[] = [
    {
      key: 'action',
      header: 'Action',
      render: (t) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => setForm({ topup: t })}
            aria-label={`Edit ${t.name}`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    { key: 'name', header: 'Topup Name', sortable: true, render: (t) => t.name },
    { key: 'carrier', header: 'Carrier', render: (t) => t.carrier.code },
    { key: 'paymentType', header: 'Payment Type', render: (t) => t.paymentType },
    { key: 'wholesaleCode', header: 'Wholesale Topup Code', render: (t) => t.wholesaleCode },
    {
      key: 'stateCount',
      header: 'Total State Configured',
      align: 'right',
      render: (t) => t.states.length,
    },
    { key: 'type', header: 'Type', render: (t) => t.type },
    {
      key: 'costCents',
      header: 'Topup Cost',
      sortable: true,
      align: 'right',
      render: (t) => formatCents(t.costCents),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (t) => <StatusChip status={t.status} />,
    },
    {
      key: 'voiceMinutes',
      header: 'Voice(Minutes)',
      align: 'right',
      render: (t) => t.voiceMinutes,
    },
    { key: 'textCount', header: 'Text', align: 'right', render: (t) => t.textCount },
    { key: 'dataMb', header: 'Data(MB)', align: 'right', render: (t) => t.dataMb },
    { key: 'states', header: 'State', render: (t) => <StateList states={t.states} /> },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          component="a"
          href={topupsExportUrl({ ...list.applied, sort: list.sort })}
          download
        >
          Download
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setForm({})}>
          Add Topup
        </Button>
      </Box>

      <FilterPanel onSearch={list.search} onReset={list.reset}>
        <TextField
          label="Topup Name"
          value={list.draft.name}
          onChange={(e) => list.setField('name', e.target.value)}
        />
        <TextField
          select
          label="State"
          value={list.draft.state}
          onChange={(e) => list.setField('state', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {STATE_OPTIONS.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          value={list.draft.status}
          onChange={(e) => list.setField('status', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {RECORD_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Carrier"
          value={list.draft.carrierId}
          onChange={(e) => list.setField('carrierId', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {carrierOptions(carriers.data).map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterPanel>

      <DataTable
        label="Topups"
        columns={columns}
        rows={topups.data?.data}
        rowKey={(t) => t.id}
        loading={topups.isFetching}
        error={topups.error}
        onRetry={() => void topups.refetch()}
        page={list.page}
        pageSize={list.pageSize}
        total={topups.data?.total ?? 0}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        sort={list.sort}
        onSortChange={list.setSort}
      />

      <TopupFormDialog open={Boolean(form)} topup={form?.topup} onClose={() => setForm(null)} />
    </Box>
  );
}
